import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { ActiveTableButton } from '@ps/web-poker-active-games';
import { useTrackers } from '@ps/web-shared-analytics';
import { InfoTooltip, StarsButton } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BalanceAmount from '@swf/components/CurrencyAmount/BalanceAmount';
import StarsRewards from '@swf/components/StarsRewards/StarsRewards';
import AccountIcon from '@swf/core/assets/icons/account-selected.svg';
import SessionSummaryIcon from '@swf/core/assets/icons/alternates/info.svg';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { CircularProgressBar, LoyaltyWidgetLobby } from '@swf/martech';

import {
  TXTCLI_Current_Session,
  TXTCLI_Deposit,
  TXTCLI_Deposit_37
} from './LoggedIn.messages.json';
import styles from './LoggedIn.scss';

export interface ClassesProp {
  account?: string;
  balance?: string;
  deposit?: string;
  rewards?: string;
}

interface Props {
  classes?: ClassesProp;
}

const LoggedIn: React.FC<Props> = ({ classes }) => {
  const loyaltyWidgetRefresh = useFeature(FeatureFlag.LoyaltyWidgetRefresh);
  const loyaltyWidgetCasinoSports = useFeature(FeatureFlag.LoyaltyWidgetCasinoSports);
  const isChestWidgetEnabled = useFeature(FeatureFlag.ChestWidgetLobby);
  const site = useSelector(select.cms.getSiteId);
  const isPoker = useSelector(select.cms.getBrandId) === BrandTypes.POKER_STARS;
  const userId = useSelector(select.account.getUserId);
  const playChips = useSelector(select.account.getPlayChips);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const { showBalance } = useSelector(select.localSettings.get);
  const totalBalance = useSelector(select.account.getTotalBalance);
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);
  const balance = isPlayMoney ? playChips : totalBalance;
  const { buttonPressed } = useTrackers();
  const isExternalPartnerSite = useSelector(select.cms.isExternalPartnerSite);
  const depositTranslation = isExternalPartnerSite ? TXTCLI_Deposit_37 : TXTCLI_Deposit;

  const { modal } = useDispatch<Dispatch>();

  return userId && !loading ? (
    <>
      {isChestWidgetEnabled ? (
        <CircularProgressBar size={42} strokeWidth={3} showNudge pointToChestInventory />
      ) : (
        (isPoker || loyaltyWidgetCasinoSports) &&
        (loyaltyWidgetRefresh ? <LoyaltyWidgetLobby /> : <StarsRewards />)
      )}
      {[Sites.CA_ON, Sites.NL].includes(site) && (
        <div className={styles.summaryWrapper}>
          <InfoTooltip addHoverEffect offset={[0, 15]} content={[TXTCLI_Current_Session]}>
            <StarsButton
              className={styles.summaryIcon}
              icon={<SessionSummaryIcon />}
              onClick={() => {
                buttonPressed({ buttonName: 'Session Summary' });
                modal.enqueue({ path: '/notifications/session-summary-notification' });
              }}
            />
          </InfoTooltip>
        </div>
      )}

      <ActiveTableButton />

      {showBalance && (
        <button
          data-testid="header-cashier-button"
          className={classNames(styles.balance, classes?.balance)}
          onClick={() => {
            buttonPressed({ buttonName: 'Balance' });
            modal.enqueue({ path: `/account/cashier/${isPlayMoney ? 'playmoney' : 'home'}` });
          }}
        >
          <BalanceAmount
            hasSmallLineHeightClass
            value={balance as number}
            decimalClassName={styles.decimal}
          />
        </button>
      )}
      <button
        className={classNames(styles.account, classes?.account)}
        onClick={() => {
          buttonPressed({ buttonName: 'Account' });
          modal.enqueue({ path: '/account' });
        }}
      >
        <AccountIcon className={styles.icon} />
        <span className={styles.userId}>{userId}</span>
      </button>
      {!isSportsSelfExcluded && (
        <StarsButton
          className={classNames(styles.deposit, classes?.deposit)}
          size="small"
          onClick={() => modal.enqueue({ path: '/account/cashier/deposit' })}
          translation={depositTranslation}
        />
      )}
    </>
  ) : null;
};

export default LoggedIn;
